import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShoppingBag, Users, Download, DollarSign, TrendingUp, AlertTriangle, ArrowUpCircle } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (n != null ? (+n).toLocaleString("es-EC") : "—");
const fmt2 = (n) => (n != null ? (+n).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : "—");
const statusColor = (s) => {
  if (s === "bajo_minimo") return "bg-[#fdecea] text-[#b53a25] border-[#d99584]";
  if (s === "sobre_techo") return "bg-[#eaf3fd] text-[#1f5aa8] border-[#a8c5e5]";
  if (s === "ok") return "bg-[#e9f2ee] text-[#205c45] border-[#9ec4b1]";
  return "bg-[#f5f8f6] text-[#5c7368] border-[#d1dbd6]";
};
const statusLabel = (s) => ({ bajo_minimo: "🔴 Bajo mín.", sobre_techo: "🔵 Sobre techo", ok: "🟢 OK", sin_ref: "— sin ref" }[s] || "—");

/** Modal: historial de ventas de un producto comercial.
 *  Muestra agregado por cliente + detalle de movimientos. Permite exportar CSV. */
export default function HistorialVentasProductoDialog({ producto, onClose }) {
  const [desde, setDesde] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 6);
    return d.toISOString().slice(0, 10);
  });
  const [hasta, setHasta] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("clientes"); // clientes | detalle | precios

  const load = async () => {
    if (!producto?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/masters/productos-comerciales/${producto.id}/historial-ventas`, {
        params: { desde, hasta },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (producto) load(); /* eslint-disable-next-line */ }, [producto, desde, hasta]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [];
    rows.push([`Historial de ventas - ${producto.nombre} (${producto.codigo})`]);
    rows.push([`Rango: ${desde} a ${hasta}`]);
    rows.push([]);
    rows.push(["Agregado por cliente:"]);
    rows.push(["Cliente", "Cantidad total", "# Facturas", "Última compra", "Facturas"]);
    (data.por_cliente || []).forEach(c => {
      rows.push([c.cliente_nombre, c.cantidad_total, c.n_facturas, c.ultima_compra || "", (c.facturas || []).join(" | ")]);
    });
    rows.push([]);
    rows.push(["Detalle de movimientos:"]);
    rows.push(["Fecha", "Factura", "Cliente", "Lote", "Cantidad", "Unidad"]);
    (data.detalle || []).forEach(m => {
      rows.push([m.fecha, m.documento_numero, m.cliente_nombre, m.numero_lote, m.cantidad, m.unidad]);
    });
    const csv = "\ufeff" + rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ventas-${producto.codigo}-${desde}_${hasta}.csv`;
    link.click();
  };

  if (!producto) return null;

  return (
    <Dialog open={!!producto} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="hist-ventas-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag size={18} className="text-[#205c45]" />
            Historial de ventas · {producto.codigo}
          </DialogTitle>
          <div className="text-sm text-[#5c7368]">{producto.nombre}</div>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2 bg-white rounded border p-2">
            <div>
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="h-9 w-40" />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="h-9 w-40" />
            </div>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-9">
              {loading && <Loader2 className="animate-spin mr-1" size={12} />} Actualizar
            </Button>
          </div>

          {data && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#e9f2ee] rounded p-3 border border-[#9ec4b1]">
                  <div className="text-[10px] uppercase text-[#205c45]">Total vendido</div>
                  <div className="text-2xl font-bold tabular-nums text-[#205c45]">{fmt(data.total_vendido)} u.</div>
                </div>
                <div className="bg-[#fef7ec] rounded p-3 border border-[#f5c477]">
                  <div className="text-[10px] uppercase text-[#7a4000]"># Movimientos</div>
                  <div className="text-2xl font-bold tabular-nums text-[#7a4000]">{data.n_movimientos}</div>
                </div>
                <div className="bg-[#f5f8f6] rounded p-3 border">
                  <div className="text-[10px] uppercase text-[#5c7368]">Clientes distintos</div>
                  <div className="text-2xl font-bold tabular-nums">{data.n_clientes}</div>
                </div>
              </div>

              {/* KPIs de precios/márgenes ITER185 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="rounded p-2 border bg-white">
                  <div className="text-[10px] uppercase text-[#5c7368] flex items-center gap-1"><DollarSign size={10}/>Precio promedio</div>
                  <div className="text-lg font-bold tabular-nums text-[#15241d]" data-testid="hv-kpi-precio-prom">${fmt2(data.precio_promedio)}</div>
                  <div className="text-[10px] text-[#8ba397]">min ${fmt2(data.precio_min)} · max ${fmt2(data.precio_max)}</div>
                </div>
                <div className="rounded p-2 border bg-white">
                  <div className="text-[10px] uppercase text-[#5c7368] flex items-center gap-1"><TrendingUp size={10}/>Margen promedio</div>
                  <div className="text-lg font-bold tabular-nums text-[#15241d]" data-testid="hv-kpi-margen-prom">{data.margen_promedio != null ? `${data.margen_promedio.toFixed(1)}%` : "—"}</div>
                  <div className="text-[10px] text-[#8ba397]">costo ${fmt2(data.costo_unidad)}</div>
                </div>
                <div className="rounded p-2 border bg-white">
                  <div className="text-[10px] uppercase text-[#5c7368] flex items-center gap-1"><AlertTriangle size={10}/>Bajo mínimo</div>
                  <div className={`text-lg font-bold tabular-nums ${data.n_bajo_minimo > 0 ? "text-[#b53a25]" : "text-[#15241d]"}`} data-testid="hv-kpi-bajo-min">{data.n_bajo_minimo}</div>
                  <div className="text-[10px] text-[#8ba397]">P.Mín ${fmt2(data.precio_minimo)}</div>
                </div>
                <div className="rounded p-2 border bg-white">
                  <div className="text-[10px] uppercase text-[#5c7368] flex items-center gap-1"><ArrowUpCircle size={10}/>Sobre techo</div>
                  <div className={`text-lg font-bold tabular-nums ${data.n_sobre_techo > 0 ? "text-[#1f5aa8]" : "text-[#15241d]"}`} data-testid="hv-kpi-sobre-techo">{data.n_sobre_techo}</div>
                  <div className="text-[10px] text-[#8ba397]">Techo ${fmt2(data.precio_techo)}</div>
                </div>
              </div>

              <div className="flex gap-2 border-b">
                <button onClick={() => setTab("clientes")} className={`px-3 py-1.5 text-sm border-b-2 ${tab === "clientes" ? "border-[#205c45] text-[#205c45] font-semibold" : "border-transparent text-[#5c7368]"}`} data-testid="tab-clientes">
                  <Users size={14} className="inline mr-1" /> Por cliente
                </button>
                <button onClick={() => setTab("precios")} className={`px-3 py-1.5 text-sm border-b-2 ${tab === "precios" ? "border-[#205c45] text-[#205c45] font-semibold" : "border-transparent text-[#5c7368]"}`} data-testid="tab-precios">
                  <DollarSign size={14} className="inline mr-1" /> Precios y márgenes
                </button>
                <button onClick={() => setTab("detalle")} className={`px-3 py-1.5 text-sm border-b-2 ${tab === "detalle" ? "border-[#205c45] text-[#205c45] font-semibold" : "border-transparent text-[#5c7368]"}`} data-testid="tab-detalle">
                  Detalle ({data.n_movimientos})
                </button>
              </div>

              {tab === "clientes" && (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f5f8f6] text-xs">
                      <tr>
                        <th className="text-left p-2">Cliente</th>
                        <th className="text-right p-2">Cantidad</th>
                        <th className="text-right p-2"># Facturas</th>
                        <th className="text-left p-2">Última compra</th>
                        <th className="text-left p-2">Facturas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.por_cliente || []).map((c, i) => (
                        <tr key={i} className="border-t hover:bg-[#fafafa]">
                          <td className="p-2 font-medium">{c.cliente_nombre}</td>
                          <td className="p-2 text-right tabular-nums font-semibold">{fmt(c.cantidad_total)}</td>
                          <td className="p-2 text-right tabular-nums">{c.n_facturas}</td>
                          <td className="p-2 text-xs">{c.ultima_compra || "—"}</td>
                          <td className="p-2 text-[10px] font-mono">
                            {(c.facturas || []).slice(0, 5).map(f => <Badge key={f} variant="outline" className="mr-1 mb-1 text-[10px]">{f}</Badge>)}
                            {(c.facturas || []).length > 5 && <span className="text-[#8ba397]"> +{c.facturas.length - 5}</span>}
                          </td>
                        </tr>
                      ))}
                      {(data.por_cliente || []).length === 0 && (
                        <tr><td colSpan={5} className="text-center py-8 text-[#8ba397]">Sin ventas en este rango</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "precios" && (
                <div className="border rounded overflow-hidden max-h-96 overflow-y-auto" data-testid="hv-precios-tab">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f5f8f6] text-xs sticky top-0">
                      <tr>
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Factura</th>
                        <th className="text-left p-2">Cliente</th>
                        <th className="text-left p-2">Vendedor</th>
                        <th className="text-right p-2">Cant.</th>
                        <th className="text-right p-2">P.U.</th>
                        <th className="text-right p-2">Desc.</th>
                        <th className="text-right p-2">Margen</th>
                        <th className="text-center p-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.detalle || []).map((m, i) => (
                        <tr key={i} className="border-t hover:bg-[#fafafa]" data-testid={`hv-precio-row-${i}`}>
                          <td className="p-2 text-xs">{m.fecha}</td>
                          <td className="p-2 font-mono text-xs">{m.documento_numero || "—"}</td>
                          <td className="p-2 text-xs">{m.cliente_nombre || "—"}</td>
                          <td className="p-2 text-xs text-[#5c7368]">{m.vendedor_nombre || "—"}</td>
                          <td className="p-2 text-right tabular-nums">{m.cantidad} <span className="text-[10px] text-[#5c7368]">{m.unidad?.charAt(0)}</span></td>
                          <td className="p-2 text-right tabular-nums font-semibold">
                            {m.precio_unitario_normalizado != null ? `$${fmt2(m.precio_unitario_normalizado)}` : "—"}
                            {m.unidad === "caja" && m.precio_unitario != null && (
                              <div className="text-[9px] text-[#5c7368]">caja ${fmt2(m.precio_unitario)}</div>
                            )}
                          </td>
                          <td className="p-2 text-right tabular-nums text-[#5c7368]">{m.descuento_pct != null ? `${(+m.descuento_pct).toFixed(1)}%` : "—"}</td>
                          <td className="p-2 text-right tabular-nums font-semibold" style={{ color: m.margen_pct != null && m.margen_pct < 10 ? "#b53a25" : (m.margen_pct != null && m.margen_pct < 25 ? "#8a5500" : "#205c45") }}>
                            {m.margen_pct != null ? `${m.margen_pct.toFixed(1)}%` : "—"}
                          </td>
                          <td className="p-2 text-center">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusColor(m.status)}`}>{statusLabel(m.status)}</span>
                          </td>
                        </tr>
                      ))}
                      {(data.detalle || []).length === 0 && (
                        <tr><td colSpan={9} className="text-center py-8 text-[#8ba397]">Sin movimientos en este rango</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "detalle" && (
                <div className="border rounded overflow-hidden max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f5f8f6] text-xs sticky top-0">
                      <tr>
                        <th className="text-left p-2">Fecha</th>
                        <th className="text-left p-2">Factura</th>
                        <th className="text-left p-2">Cliente</th>
                        <th className="text-left p-2">Lote</th>
                        <th className="text-right p-2">Cant.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.detalle || []).map((m, i) => (
                        <tr key={i} className="border-t hover:bg-[#fafafa]">
                          <td className="p-2 text-xs">{m.fecha}</td>
                          <td className="p-2 font-mono text-xs">{m.documento_numero || "—"}</td>
                          <td className="p-2">{m.cliente_nombre || "—"}</td>
                          <td className="p-2 font-mono text-xs">{m.numero_lote || "—"}</td>
                          <td className="p-2 text-right tabular-nums">{m.cantidad} <span className="text-[10px] text-[#5c7368]">{m.unidad}</span></td>
                        </tr>
                      ))}
                      {(data.detalle || []).length === 0 && (
                        <tr><td colSpan={5} className="text-center py-8 text-[#8ba397]">Sin movimientos</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={exportCSV} disabled={!data} data-testid="exportar-historial-ventas">
            <Download size={14} className="mr-1" /> Exportar CSV
          </Button>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
